import {
  Children,
  FunctionComponent,
  isValidElement,
  MouseEventHandler,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import useStyles from './styles'
import { DropdownProps, IDropdownContext } from './types'
import { ReactComponent as Arrow } from '@/assets/icon_arrow_down_24.svg'
import { ReactComponent as ErrorIcon } from '@/assets/icon_error.svg'
import { OptionProps, OptionValue } from '@/components/Option/types'
import { useStateStyles } from './utils'
import { isOptionValue } from '../Option/utils'
import { DropdownContext } from './DropdownContext'

export const Dropdown: FunctionComponent<DropdownProps> = ({
  label,
  placeholder,
  styles: overrides,
  onChange,
  required,
  disabled,
  children,
  value: externalValue,
  initialValue,
  inputRef,
  fullWidth = false,
  popoverMaxHeight,
  error,
  errorMessage,
}) => {
  const [internalValue, setValue] = useState<string | number>(
    initialValue || '',
  )
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [active, setActive] = useState<boolean>(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [mapValueLabels, setMapValueLabels] = useState(
    new Map<OptionValue, string>(),
  )

  const registerLabel = useCallback<IDropdownContext['registerLabel']>(
    ({ value, label }) => {
      setMapValueLabels(map => {
        map.set(value, label)
        return new Map(map)
      })
    },
    [],
  )

  const value = useMemo(
    () => (isOptionValue(externalValue) ? externalValue : internalValue),
    [externalValue, internalValue],
  )

  const styles = useStyles({
    isOpen,
    fullWidth,
    hasLabel: !!label,
    popoverMaxHeight,
  })

  const {
    activeStyles,
    dataEnteredStyles,
    expandedStyles,
    disabledStyles,
    errorStyles,
  } = useStateStyles({
    active,
    disabled,
    error,
    value,
    styles,
    overrides,
  })

  const handleToggle = useCallback((value?: boolean) => {
    setIsOpen(prevState => (typeof value === 'boolean' ? value : !prevState))
    setActive(prevState => (typeof value === 'boolean' ? value : !prevState))
  }, [])

  const handleChange = useCallback(
    (value: OptionValue) => {
      setValue(value)
      onChange && onChange(value)
    },
    [onChange],
  )

  const childrensArray = useMemo(() => Children.toArray(children), [children])

  const validChildrens = useMemo<ReactElement<OptionProps>[]>(
    () =>
      childrensArray.filter(child => {
        const isValid = isValidElement(child)
        if (!isValid) {
          console.warn('Select children must to be Option components')
        }

        return isValid
      }) as ReactElement<OptionProps>[],
    [childrensArray],
  )

  const handleOptionClick = useCallback(
    (value: OptionValue) => {
      handleChange(value)
      handleToggle(false)
    },
    [handleChange, handleToggle],
  )

  const handlePlaceholderClick: MouseEventHandler<HTMLDivElement> = useCallback(
    event => {
      if (isOpen) {
        wrapperRef.current.blur()
        event.preventDefault()
      }
    },
    [isOpen],
  )

  const currentLabel = useMemo(() => {
    return mapValueLabels.get(value) || value
  }, [mapValueLabels, value])

  return (
    <DropdownContext.Provider
      value={{ value, onChange: handleOptionClick, registerLabel }}
    >
      <div css={[styles.root, overrides?.root]} className="hero-dropdown__root">
        <div
          data-testid="hero-dropdown"
          className="hero-dropdown__wrapper"
          css={[
            styles.wrapper,
            overrides?.wrapper,
            ...disabledStyles,
            ...activeStyles,
            ...dataEnteredStyles,
            ...expandedStyles,
            ...errorStyles,
          ]}
          onFocus={() => !disabled && handleToggle(true)}
          onBlur={event => {
            if (wrapperRef.current.contains(event.relatedTarget as Node)) return
            handleToggle(false)
          }}
          tabIndex={0}
          ref={wrapperRef}
        >
          {label && (
            <label
              css={[styles.label, overrides?.label, ...disabledStyles]}
              className="hero-dropdown__label"
            >
              {label}
            </label>
          )}
          <span
            css={[
              styles.placeholder,
              overrides?.placeholder,
              ...disabledStyles,
            ]}
            className="hero-dropdown__placeholder"
            onMouseDown={handlePlaceholderClick}
          >
            <p>{currentLabel || placeholder}</p>
            <Arrow />
          </span>
          <div css={[styles.select, overrides?.select]} tabIndex={-1}>
            {validChildrens}
          </div>
          <input
            css={styles.nativeInput}
            tabIndex={-1}
            value={value}
            required={required}
            ref={inputRef}
            onChange={() => null}
            disabled={disabled}
          />
        </div>
        {error && errorMessage && (
          <div
            css={[styles.errorMessage, overrides?.errorMessage]}
            className="hero-dropdown__error"
          >
            <ErrorIcon />
            <p>{errorMessage}</p>
          </div>
        )}
      </div>
    </DropdownContext.Provider>
  )
}
